import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { useLanguage } from '../../contexts/LanguageContext';
import { cn } from '../../lib/cn';
import { formatDate, formatNumber } from '../../lib/format';
import type { PathBand, PathCountryFit, PathDiagnosis, PathExplanation, PathScore } from '../../types';

const BAND_TONE: Record<PathBand, { bar: string; chip: string }> = {
  low: { bar: 'bg-status-success', chip: 'bg-status-success/10 text-emerald-700 dark:text-emerald-300' },
  medium: { bar: 'bg-status-warning', chip: 'bg-status-warning/15 text-amber-800 dark:text-amber-300' },
  high: { bar: 'bg-orange-500', chip: 'bg-orange-500/10 text-orange-800 dark:text-orange-300' },
  critical: { bar: 'bg-status-danger', chip: 'bg-status-danger/10 text-red-700 dark:text-red-300' },
  unknown: { bar: 'bg-border', chip: 'bg-elevated text-text-muted' },
};

const READINESS_TONE = {
  strong: 'bg-status-success/10 text-emerald-700 dark:text-emerald-300',
  possible: 'bg-brand-primary/10 text-brand-primary',
  risky: 'bg-status-warning/15 text-amber-800 dark:text-amber-300',
  not_recommended_now: 'bg-status-danger/10 text-red-700 dark:text-red-300',
} as const;

const SEVERITY_TONE = {
  high: 'border-status-danger/30 bg-status-danger/5',
  medium: 'border-status-warning/40 bg-status-warning/5',
  low: 'border-border bg-surface',
} as const;

/** Turns a coded explanation into a sentence in the current language. */
function useExplain() {
  const { t } = useTranslation();
  const { language } = useLanguage();
  const fmtValue = (key: string, value: unknown): string => {
    if (Array.isArray(value)) {
      const items = value.map((v) => t(`path.doc.${v}`, { defaultValue: t(`path.input.${v}`, { defaultValue: String(v) }) }));
      return items.length ? items.join(language === 'fa' ? '، ' : ', ') : t('path.explain.none');
    }
    if (typeof value === 'number') {
      if (['cost', 'budget'].includes(key)) return formatNumber(value, language, { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 });
      if (key === 'ratio') return formatNumber(value, language, { style: 'percent', maximumFractionDigits: 0 });
      return formatNumber(value, language, { maximumFractionDigits: 2 });
    }
    if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
      return formatDate(value, language, { year: 'numeric', month: 'long', day: 'numeric' });
    }
    if (typeof value === 'string' && /^\d{4}-\d{2}$/.test(value)) {
      return formatDate(`${value}-01`, language, { year: 'numeric', month: 'long' });
    }
    if (key === 'test' && typeof value === 'string') return t(`languageTests.tests.${value}`, { defaultValue: value });
    return String(value ?? '');
  };
  return (e: PathExplanation | { code: string; params: Record<string, unknown> }, prefix = 'path.explain') => {
    const raw = e.params ?? {};
    const params = Object.fromEntries(Object.entries(raw).map(([k, v]) => [k, fmtValue(k, v)]));
    // "documents_missing" with nothing missing reads better as its own sentence.
    const empty = Object.values(raw).some((v) => Array.isArray(v) && v.length === 0);
    const keys = empty ? [`${prefix}.${e.code}_empty`, `${prefix}.${e.code}`] : [`${prefix}.${e.code}`];
    return t(keys, { ...params, defaultValue: e.code });
  };
}

function ScoreRow({ name, score }: { name: string; score: PathScore }) {
  const { t } = useTranslation();
  const explain = useExplain();
  const tone = BAND_TONE[score.band];
  return (
    <li className="space-y-1.5 px-4 py-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="text-sm font-medium text-text-primary">{t(`path.scores.${name}`)}</span>
        <span className={cn('rounded-full px-2 py-0.5 text-xs font-medium', tone.chip)}>{t(`path.band.${score.band}`)}</span>
      </div>
      <div className="h-1.5 w-full overflow-hidden rounded-full bg-elevated" role="presentation">
        <div className={cn('h-full rounded-full', tone.bar)} style={{ width: `${score.band === 'unknown' ? 0 : Math.max(4, score.score)}%` }} />
      </div>
      <p className="text-sm text-text-secondary">{explain(score.explanation)}</p>
    </li>
  );
}

function CountryRow({ fit, avoid }: { fit: PathCountryFit; avoid?: boolean }) {
  const { t } = useTranslation();
  const { language } = useLanguage();
  const explain = useExplain();
  return (
    <li className="flex flex-col gap-1 px-4 py-3 sm:flex-row sm:items-start sm:justify-between">
      <div className="min-w-0">
        <p className="font-medium text-text-primary">{fit.country}</p>
        <p className="text-sm text-text-secondary">
          {[
            // The budget is the same on every row; the share of it each country costs is what differs.
            ...(fit.budget_ratio != null ? [explain({ code: 'share_of_budget', params: { ratio: fit.budget_ratio } }, 'path.reason')] : []),
            ...fit.reasons.filter((r) => r.code !== 'cost_vs_budget').map((r) => explain(r, 'path.reason')),
          ].join(language === 'fa' ? '، ' : ' · ')}
        </p>
      </div>
      <div className="shrink-0 text-sm text-text-secondary sm:text-end">
        {fit.annual_cost_eur != null && (
          <p>{t('path.countries.perYear', { amount: formatNumber(fit.annual_cost_eur, language, { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }) })}</p>
        )}
        {!avoid && fit.programs_in_field > 0 && (
          <p className="text-xs text-text-muted">{t('path.countries.programs', { count: fit.programs_in_field, n: formatNumber(fit.programs_in_field, language) })}</p>
        )}
      </div>
    </li>
  );
}

/** Links to /path mean "edit the profile": open the form instead of reloading the page. */
function ActionLink({ to, onEdit, className, children }: { to: string; onEdit: () => void; className: string; children: React.ReactNode }) {
  if (to === '/path') {
    return <button type="button" onClick={onEdit} className={className}>{children}</button>;
  }
  return <Link to={to} className={className}>{children}</Link>;
}

export function PathDiagnosisView({
  diagnosis,
  onEdit,
  onRecalculate,
  recalculating,
  onDelete,
  deleting,
}: {
  diagnosis: PathDiagnosis;
  onEdit: () => void;
  onRecalculate: () => void;
  recalculating: boolean;
  onDelete: () => void;
  deleting: boolean;
}) {
  const { t } = useTranslation();
  const [confirmDelete, setConfirmDelete] = useState(false);
  const { language } = useLanguage();
  const explain = useExplain();
  const [first, ...rest] = diagnosis.next_actions;

  return (
    <div className="space-y-8">
      <header className="space-y-3">
        <p className="text-sm text-text-secondary">{t('path.yourRoute')}</p>
        <h1 className="text-2xl font-bold text-text-primary [text-wrap:balance]">{t(`path.type.${diagnosis.recommended_path_type}.title`)}</h1>
        <p className="max-w-[65ch] text-text-secondary [text-wrap:pretty]">{t(`path.type.${diagnosis.recommended_path_type}.body`)}</p>
        <div className="flex flex-wrap items-center gap-2">
          <span className={cn('rounded-full px-2.5 py-1 text-xs font-semibold', READINESS_TONE[diagnosis.readiness.band])}>
            {t(`path.readiness.${diagnosis.readiness.band}`)}
          </span>
          <span className="text-xs text-text-muted">{t(`path.risk.${diagnosis.route_risk_level}`)}</span>
          {diagnosis.target_deadline && (
            <span className="text-xs text-text-muted">
              {t(diagnosis.target_deadline_estimated ? 'path.deadlineEstimated' : 'path.deadlineTracked', {
                date: formatDate(diagnosis.target_deadline, language, { year: 'numeric', month: 'long', day: 'numeric' }),
              })}
            </span>
          )}
        </div>
      </header>

      {first && (
        <section aria-labelledby="next-step" className="rounded-xl border border-brand-primary/30 bg-brand-primary/5 p-5">
          <h2 id="next-step" className="text-sm font-medium text-brand-primary">{t('path.nextStep')}</h2>
          <p className="mt-1 text-lg font-semibold text-text-primary">{explain(first, 'path.action')}</p>
          <p className="mt-1 max-w-[65ch] text-sm text-text-secondary">{explain(first, 'path.actionWhy')}</p>
          {first.link && (
            <ActionLink
              to={first.link}
              onEdit={onEdit}
              className="mt-4 inline-flex rounded-lg bg-brand-primary px-4 py-2 text-sm font-medium text-white hover:bg-brand-secondary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary focus-visible:ring-offset-2"
            >
              {t(first.link === '/path' ? 'path.completeNow' : 'path.go')}
            </ActionLink>
          )}
        </section>
      )}

      {diagnosis.top_blockers.length > 0 && (
        <section aria-labelledby="blockers" className="space-y-3">
          <h2 id="blockers" className="text-lg font-semibold text-text-primary">{t('path.blockersTitle')}</h2>
          <ul className="space-y-2">
            {diagnosis.top_blockers.map((b) => (
              <li key={b.code} className={cn('rounded-xl border px-4 py-3', SEVERITY_TONE[b.severity])}>
                <p className="text-sm font-medium text-text-primary">{explain(b, 'path.blocker')}</p>
                {b.link && (
                  <Link to={b.link} className="text-sm text-brand-primary underline underline-offset-2">{t('path.learnMore')}</Link>
                )}
              </li>
            ))}
          </ul>
        </section>
      )}

      {rest.length > 0 && (
        <section aria-labelledby="then" className="space-y-3">
          <h2 id="then" className="text-lg font-semibold text-text-primary">{t('path.thenTitle')}</h2>
          <ol className="divide-y divide-border overflow-hidden rounded-xl border border-border bg-surface">
            {rest.map((a, i) => (
              <li key={a.code} className="flex items-start gap-3 px-4 py-3">
                <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-elevated text-xs font-semibold text-text-secondary">
                  {formatNumber(i + 2, language)}
                </span>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-text-primary">{explain(a, 'path.action')}</p>
                  {a.link && (
                    <ActionLink to={a.link} onEdit={onEdit} className="text-sm text-brand-primary underline underline-offset-2">
                      {t(a.link === '/path' ? 'path.completeNow' : 'path.go')}
                    </ActionLink>
                  )}
                </div>
              </li>
            ))}
          </ol>
        </section>
      )}

      <section aria-labelledby="why" className="space-y-3">
        <h2 id="why" className="text-lg font-semibold text-text-primary">{t('path.whyTitle')}</h2>
        <ul className="divide-y divide-border overflow-hidden rounded-xl border border-border bg-surface">
          <ScoreRow name="language_gap" score={diagnosis.language_gap} />
          <ScoreRow name="document_gap" score={diagnosis.document_gap} />
          <ScoreRow name="timeline_pressure" score={diagnosis.timeline_pressure} />
          <ScoreRow name="cost_pressure" score={diagnosis.cost_pressure} />
        </ul>
      </section>

      {(diagnosis.recommended_countries.length > 0 || diagnosis.countries_to_avoid_for_now.length > 0) && (
        <section aria-labelledby="countries" className="grid gap-6 lg:grid-cols-2">
          <div className="space-y-3">
            <h2 id="countries" className="text-lg font-semibold text-text-primary">{t('path.countries.fit')}</h2>
            {diagnosis.recommended_countries.length ? (
              <ul className="divide-y divide-border overflow-hidden rounded-xl border border-border bg-surface">
                {diagnosis.recommended_countries.map((c) => <CountryRow key={c.country} fit={c} />)}
              </ul>
            ) : (
              <p className="rounded-xl border border-dashed border-border bg-surface px-4 py-4 text-sm text-text-secondary">{t('path.countries.none')}</p>
            )}
          </div>
          {diagnosis.countries_to_avoid_for_now.length > 0 && (
            <div className="space-y-3">
              <h2 className="text-lg font-semibold text-text-primary">{t('path.countries.avoid')}</h2>
              <ul className="divide-y divide-border overflow-hidden rounded-xl border border-border bg-surface">
                {diagnosis.countries_to_avoid_for_now.map((c) => <CountryRow key={c.country} fit={c} avoid />)}
              </ul>
            </div>
          )}
        </section>
      )}

      <section aria-labelledby="confidence" className="rounded-xl border border-border bg-elevated px-4 py-3 text-sm text-text-secondary">
        <h2 id="confidence" className="font-medium text-text-primary">{t(`path.confidence.${diagnosis.data_confidence.level}`)}</h2>
        {diagnosis.data_confidence.missing_inputs.length > 0 && (
          <p className="mt-1">{t('path.confidence.missing', { inputs: diagnosis.data_confidence.missing_inputs.map((i) => t(`path.input.${i}`)).join(language === 'fa' ? '، ' : ', ') })}</p>
        )}
        {diagnosis.data_confidence.notes.map((n) => <p key={n.code} className="mt-1">{explain(n, 'path.note')}</p>)}
        <p className="mt-2 text-xs text-text-muted">{t('path.notAdvice')}</p>
      </section>

      <div className="flex flex-wrap gap-3">
        <button onClick={onEdit} className="rounded-lg border border-brand-primary px-4 py-2 text-sm font-medium text-brand-primary hover:bg-elevated">
          {t('path.edit')}
        </button>
        <button onClick={onRecalculate} disabled={recalculating} className="rounded-lg px-4 py-2 text-sm font-medium text-text-secondary hover:bg-elevated disabled:opacity-50">
          {recalculating ? t('common.saving') : t('path.recalculate')}
        </button>
        {!confirmDelete && (
          <button onClick={() => setConfirmDelete(true)} className="rounded-lg px-4 py-2 text-sm font-medium text-text-secondary hover:bg-elevated hover:text-status-danger sm:ms-auto">
            {t('path.deleteAnswers')}
          </button>
        )}
      </div>

      {confirmDelete && (
        <div role="alertdialog" aria-labelledby="delete-path" className="rounded-xl border border-status-danger/30 bg-status-danger/5 p-4">
          <p id="delete-path" className="text-sm text-text-primary">{t('path.deleteConfirm')}</p>
          <div className="mt-3 flex flex-wrap gap-3">
            <button onClick={onDelete} disabled={deleting} className="rounded-lg bg-status-danger px-4 py-2 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50">
              {deleting ? t('common.saving') : t('path.deleteYes')}
            </button>
            <button onClick={() => setConfirmDelete(false)} className="rounded-lg px-4 py-2 text-sm font-medium text-text-secondary hover:bg-elevated">
              {t('common.cancel')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
